import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Utensils, Lock, CalendarDays, History as HistoryIcon, Sun } from "lucide-react";
import { linkifyMealItem, recipeUrl, fetchRecipeIndex } from "@/lib/recipeLink";

const TABS = [
  { key: "today", label: "Today", icon: Sun },
  { key: "week", label: "Week", icon: CalendarDays },
  { key: "history", label: "History", icon: HistoryIcon },
];

function fmtLong(iso) {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

export default function ClientHome() {
  const { me } = useOutletContext();
  const [tab, setTab] = useState("today");
  const [today, setToday] = useState(null);
  const [week, setWeek] = useState([]);
  const [history, setHistory] = useState([]);
  const [recipeIndex, setRecipeIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/app/client/schedule/today").then((r) => setToday(r.data)).catch(() => setToday(null)),
      api.get("/app/client/schedule/week").then((r) => setWeek(r.data.schedule || [])).catch(() => setWeek([])),
      api.get("/app/client/schedule/history").then((r) => setHistory(r.data || [])).catch(() => setHistory([])),
      fetchRecipeIndex().then((idx) => setRecipeIndex(idx)),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="pt-8 text-center">Loading your plan…</div>;

  const client = today?.client;

  return (
    <div>
      <div className="pt-2">
        <div className="section-eyebrow">Hi {me.user.name?.split(" ")[0] || "there"} 👋</div>
        <h1 className="mt-1 font-display text-3xl leading-tight" style={{ color: "var(--primary)" }}>Your diet plan</h1>
        {client?.goal && <p className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>Goal: {client.goal}</p>}
      </div>

      {/* Tabs */}
      <div className="mt-5 flex gap-1 p-1 rounded-full" style={{ background: "var(--bg-paper)" }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className="flex-1 flex items-center justify-center gap-1 py-2 rounded-full text-xs font-bold"
              style={{
                background: active ? "var(--primary)" : "transparent",
                color: active ? "var(--bg)" : "var(--primary)",
              }}
            >
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "today" && <TodayView data={today} recipeIndex={recipeIndex} />}
      {tab === "week" && <WeekView schedule={week} recipeIndex={recipeIndex} />}
      {tab === "history" && <HistoryView items={history} recipeIndex={recipeIndex} />}
    </div>
  );
}

function MealCard({ meal, i, locked, recipeIndex }) {
  return (
    <div data-testid={`meal-${i}`} className="card-soft p-5" style={locked ? { opacity: 0.85 } : {}}>
      <div className="flex items-center gap-2">
        {meal.time && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--yellow)", color: "var(--primary)" }}>{meal.time}</span>}
        <h3 className="font-display text-xl" style={{ color: "var(--primary)" }}>{meal.name}</h3>
      </div>
      <ul className="mt-3 space-y-1.5 text-[15px] leading-relaxed">
        {(meal.items || []).map((it, j) => {
          const segments = linkifyMealItem(it, recipeIndex);
          return (
            <li key={j} className="flex gap-2">
              <span style={{ color: "var(--red)" }}>•</span>
              <span style={{ color: "var(--text-primary)" }}>
                {segments.map((s, k) =>
                  s.slug ? (
                    <a
                      key={k}
                      href={recipeUrl(s.slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`recipe-link-${s.slug}`}
                      className="font-bold underline decoration-dotted underline-offset-2 hover:opacity-80"
                      style={{ color: "var(--red)" }}
                      title={`Recipe: ${s.name}`}
                    >{s.text}</a>
                  ) : (
                    <span key={k}>{s.text}</span>
                  )
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {meal.notes && <div className="mt-3 text-xs italic" style={{ color: "var(--text-secondary)" }}>💡 {meal.notes}</div>}
    </div>
  );
}

function TodayView({ data, recipeIndex }) {
  const day = data?.day;
  if (!day || !(day.meals || []).length) {
    return (
      <div className="mt-6 card-soft p-6 text-center">
        <Utensils size={28} style={{ color: "var(--red)" }} className="mx-auto" />
        <p className="mt-3 font-bold" style={{ color: "var(--primary)" }}>No plan for today yet</p>
        <p className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>Your dietitian will assign it shortly.</p>
      </div>
    );
  }
  return (
    <div className="mt-6 space-y-3">
      <div className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>
        {fmtLong(day.date)}
      </div>
      {(day.meals || []).map((m, i) => <MealCard key={i} meal={m} i={i} recipeIndex={recipeIndex} />)}
      {day.notes && (
        <div className="card-soft p-4 text-sm" style={{ background: "var(--yellow-soft)" }}>
          <div className="font-bold" style={{ color: "var(--primary)" }}>Note from your dietitian</div>
          <div className="mt-1" style={{ color: "var(--text-primary)" }}>{day.notes}</div>
        </div>
      )}
    </div>
  );
}

function WeekView({ schedule, recipeIndex }) {
  if (!schedule.length) {
    return (
      <div className="mt-6 card-soft p-6 text-center">
        <CalendarDays size={28} style={{ color: "var(--red)" }} className="mx-auto" />
        <p className="mt-3 font-bold" style={{ color: "var(--primary)" }}>No diet plan yet</p>
        <p className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>Your dietitian hasn't published any days yet. Once they do, they'll appear here.</p>
      </div>
    );
  }
  // Default-open the TODAY day, else the first future day, else the first entry
  const todayIdx = schedule.findIndex((d) => d.is_today);
  const futureIdx = schedule.findIndex((d) => !d.is_past);
  const defaultOpenIdx = todayIdx >= 0 ? todayIdx : (futureIdx >= 0 ? futureIdx : 0);
  return (
    <div className="mt-6 space-y-4">
      <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
        Showing all {schedule.length} published day{schedule.length === 1 ? "" : "s"} from your dietitian
      </div>
      {schedule.map((d, idx) => (
        <details key={d.date} data-testid={`week-day-${idx}`} open={idx === defaultOpenIdx} className="card-soft p-4" style={d.is_past ? { opacity: 0.85 } : {}}>
          <summary className="cursor-pointer flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
                {d.is_past && <Lock size={10} />}
                {d.is_today ? "Today" : d.is_past ? "Past day" : "Upcoming"}
                {d.is_today && <span className="px-1.5 py-0.5 rounded-full text-[10px]" style={{ background: "var(--yellow)", color: "var(--primary)" }}>TODAY</span>}
              </div>
              <div className="font-display text-lg" style={{ color: "var(--primary)" }}>{fmtLong(d.date)}</div>
            </div>
            <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
              {(d.meals || []).length} meal{(d.meals || []).length === 1 ? "" : "s"}
            </span>
          </summary>
          <div className="mt-3 space-y-3">
            {(d.meals || []).map((m, i) => <MealCard key={i} meal={m} i={i} locked={d.is_past} recipeIndex={recipeIndex} />)}
            {d.notes && (
              <div className="p-3 rounded-xl text-sm" style={{ background: "var(--yellow-soft)" }}>
                <div className="font-bold text-xs" style={{ color: "var(--primary)" }}>Note</div>
                <div style={{ color: "var(--text-primary)" }}>{d.notes}</div>
              </div>
            )}
          </div>
        </details>
      ))}
    </div>
  );
}

function HistoryView({ items, recipeIndex }) {
  if (!items.length) {
    return (
      <div className="mt-6 card-soft p-6 text-center">
        <HistoryIcon size={28} style={{ color: "var(--red)" }} className="mx-auto" />
        <p className="mt-3 font-bold" style={{ color: "var(--primary)" }}>No past diets yet</p>
        <p className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>Your past days will appear here once you have some history.</p>
      </div>
    );
  }
  return (
    <div className="mt-6 space-y-3">
      {items.map((d, idx) => (
        <details key={d.date} data-testid={`history-${idx}`} className="card-soft p-4" style={{ opacity: 0.95 }}>
          <summary className="cursor-pointer flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock size={12} style={{ color: "var(--text-secondary)" }} />
              <div>
                <div className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>Locked</div>
                <div className="font-display text-lg" style={{ color: "var(--primary)" }}>{fmtLong(d.date)}</div>
              </div>
            </div>
            <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{(d.meals || []).length} meal{(d.meals || []).length === 1 ? "" : "s"}</span>
          </summary>
          <div className="mt-3 space-y-3">
            {(d.meals || []).map((m, i) => <MealCard key={i} meal={m} i={i} locked recipeIndex={recipeIndex} />)}
            {d.notes && (
              <div className="p-3 rounded-xl text-sm" style={{ background: "var(--bg-paper)" }}>
                <div className="font-bold text-xs" style={{ color: "var(--primary)" }}>Note</div>
                <div style={{ color: "var(--text-primary)" }}>{d.notes}</div>
              </div>
            )}
          </div>
        </details>
      ))}
    </div>
  );
}
